/**
 * DiceProvider - central dice mode and manual prompt bridge
 * date_of_change: 2025-10-13
 * rule_reference: AGENTS.md (separate engine from presentation; provide GM hooks)
 */

const DICE_AUTO_STORAGE_KEY = 'vcsDiceAutoSettings';

const loadStoredDiceAuto = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const stored = JSON.parse(window.localStorage.getItem(DICE_AUTO_STORAGE_KEY) || '{}');
    return stored && typeof stored === 'object' ? stored : {};
  } catch (_) {
    return {};
  }
};

let diceAuto = {
  gmAutomaticRolls: true,
  playerAutomaticRolls: true,
  ...loadStoredDiceAuto(),
};

let requestD20Impl = null; // async ({numDice, title, prompt}) => Promise<number[]>
let requestDiceImpl = null; // async ({numDice, dieSize, title, prompt}) => Promise<number[]>
let requestConfirmImpl = null;
let requestChoiceImpl = null; // async ({ title, prompt, options }) => Promise<string|null>

export function setDiceAuto(updates = {}) {
  diceAuto = { ...diceAuto, ...updates };
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(DICE_AUTO_STORAGE_KEY, JSON.stringify(diceAuto));
    }
  } catch (_) {}
}

export function getDiceAuto() {
  return { ...diceAuto };
}

export function setRequestD20(fn) {
  if (typeof fn === 'function') requestD20Impl = fn;
}

export async function requestD20({ numDice = 1, title = 'Enter Roll', prompt = 'Enter d20 roll value' } = {}) {
  const n = Math.max(1, Math.min(2, Number(numDice) || 1));
  if (requestD20Impl) {
    return await requestD20Impl({ numDice: n, title, prompt });
  }
  // Fallback: basic prompt loop (non-ideal UX; replaced when UI binds setRequestD20)
  const vals = [];
  for (let i = 0; i < n; i++) {
    // eslint-disable-next-line no-alert
    const raw = (typeof window !== 'undefined' && window.prompt)
      ? window.prompt(`${title}\n${prompt} (#${i + 1})`, '10')
      : '10';
    const v = parseInt(raw || '10', 10);
    vals.push(Number.isFinite(v) ? v : 10);
  }
  return vals;
}

export function setRequestDice(fn) {
  if (typeof fn === 'function') requestDiceImpl = fn;
}

export async function requestDice({ numDice = 1, dieSize = 20, title = 'Enter Rolls', prompt = 'Enter dice results' } = {}) {
  const n = Math.max(1, Math.min(100, Number(numDice) || 1));
  const d = Math.max(2, Number(dieSize) || 20);
  if (requestDiceImpl) {
    return await requestDiceImpl({ numDice: n, dieSize: d, title, prompt });
  }
  // Fallback: prompt per die
  const vals = [];
  for (let i = 0; i < n; i++) {
    // eslint-disable-next-line no-alert
    const raw = (typeof window !== 'undefined' && window.prompt)
      ? window.prompt(`${title}\n${prompt} (d${d}) (#${i + 1})`, '1')
      : '1';
    const v = parseInt(raw || '1', 10);
    const clamped = Math.max(1, Math.min(d, Number.isFinite(v) ? v : 1));
    vals.push(clamped);
  }
  return vals;
}

export function setRequestConfirm(fn) {
  if (typeof fn === 'function') requestConfirmImpl = fn;
}

export async function requestConfirm({ title = 'Confirm', message = 'Proceed?', yesLabel = 'Yes', noLabel = 'No' } = {}) {
  if (requestConfirmImpl) {
    try {
      return await requestConfirmImpl({ title, message, yesLabel, noLabel });
    } catch (e) {}
  }
  
  // eslint-disable-next-line no-alert
  if (typeof window !== 'undefined' && window.confirm) {
    return window.confirm(`${title}\n\n${message}`);
  }
  return false;
}

/**
 * Bind a UI-provided selection prompt. The UI should present a dropdown to avoid typos.
 * @param {Function} fn - async ({ title, prompt, options }) => Promise<string|null>
 */
export function setRequestChoice(fn) {
  if (typeof fn === 'function') requestChoiceImpl = fn;
}

/**
 * Request a selection from a finite set of options. Returns the selected value or null if cancelled.
 * date_of_change: 2025-11-05
 * rule_reference: AGENTS.md (GM hooks; no silent fallbacks; validate choice)
 */
export async function requestChoice({ title = 'Select', prompt = 'Choose one', options = [] } = {}) {
  try {
    const opts = Array.isArray(options) ? options.map(o => String(o)) : [];
    if (opts.length === 0) return null;
    if (requestChoiceImpl) {
      const v = await requestChoiceImpl({ title, prompt, options: opts });
      return (v != null && opts.includes(String(v))) ? String(v) : null;
    }
    // Basic guarded prompt: show numbered list; accept index
    if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
      const list = opts.map((o, i) => `${i + 1}. ${o}`).join('\n');
      // eslint-disable-next-line no-alert
      const raw = window.prompt(`${title}\n${prompt}\n\n${list}\n\nEnter number:`, '1');
      if (!raw) return null;
      const idx = parseInt(raw, 10) - 1;
      if (Number.isFinite(idx) && idx >= 0 && idx < opts.length) return opts[idx];
      return null;
    }
    return null;
  } catch (_) {
    return null;
  }
}
